import { useEffect, useState } from "react";
import { executionApi, venturesApi } from "@/lib/api-client";
import { Terminal, Globe, History, Play, Loader2, ShieldCheck, Radar } from "lucide-react";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Input } from "@/components/ui/input";
import { toast } from "sonner";
import { relativeTime } from "@/lib/format";

const DEFAULT_CODE = `# Real Python — runs in an isolated sandbox (20s CPU cap)
revenues = [12000, 13500, 15100]
print("Q1 total:", sum(revenues))
print("MoM growth:", round((revenues[-1] / revenues[0] - 1) * 100, 1), "%")`;

export default function ExecutionConsole() {
  const [tab, setTab] = useState("python");

  return (
    <section className="uc-card p-6 uc-fade" data-testid="execution-console">
      <div className="flex items-center justify-between mb-4 flex-wrap gap-2">
        <div>
          <div className="uc-chip mb-2"><Terminal size={11} className="text-emerald-400"/> EXECUTION ENGINE</div>
          <h2 className="font-serif-display text-2xl">Execution Console</h2>
        </div>
        <div className="uc-chip"><ShieldCheck size={10} className="text-emerald-400"/> Sandboxed · Vault-archived</div>
      </div>

      <Tabs value={tab} onValueChange={setTab}>
        <TabsList className="bg-transparent border-b border-white/10 rounded-none p-0 h-auto justify-start gap-4 mb-4">
          {[["python", "Python", Terminal], ["browser", "Browser", Globe], ["history", "History", History]].map(([k, label, Icon]) => (
            <TabsTrigger
              key={k}
              value={k}
              data-testid={`exec-tab-${k}`}
              className="rounded-none border-b-2 border-transparent data-[state=active]:border-emerald-400 data-[state=active]:bg-transparent data-[state=active]:text-white text-white/50 px-0 pb-2 font-mono-uc text-[11px] uppercase tracking-widest flex items-center gap-1.5"
            >
              <Icon size={11}/> {label}
            </TabsTrigger>
          ))}
        </TabsList>
        <TabsContent value="python" className="mt-0"><PythonRunner /></TabsContent>
        <TabsContent value="browser" className="mt-0"><BrowserRunner /></TabsContent>
        <TabsContent value="history" className="mt-0">{tab === "history" && <RunHistory />}</TabsContent>
      </Tabs>
    </section>
  );
}

function PythonRunner() {
  const [code, setCode] = useState(DEFAULT_CODE);
  const [running, setRunning] = useState(false);
  const [result, setResult] = useState(null);

  const run = async () => {
    setRunning(true);
    setResult(null);
    try {
      const r = await executionApi.runPython(code, 20);
      setResult(r);
      if (r.ok) toast.success(`Executed in ${r.duration_ms}ms · archived to vault`);
      else toast.error(r.timed_out ? "Execution timed out" : "Execution failed — see stderr");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Run failed");
    } finally { setRunning(false); }
  };

  return (
    <div className="space-y-3">
      <textarea
        data-testid="exec-python-code"
        value={code}
        onChange={(e) => setCode(e.target.value)}
        rows={7}
        spellCheck={false}
        className="w-full bg-black border border-white/10 focus:border-emerald-500/40 outline-none p-3 font-mono-uc text-xs text-emerald-300 leading-relaxed resize-y uc-scroll"
      />
      <button
        data-testid="exec-python-run-btn"
        onClick={run}
        disabled={running || !code.trim()}
        className="flex items-center gap-2 px-4 py-2 border border-emerald-500/40 text-emerald-400 hover:bg-emerald-500/10 font-mono-uc text-[11px] uppercase tracking-widest disabled:opacity-40 transition-colors"
      >
        {running ? <Loader2 size={12} className="animate-spin"/> : <Play size={12}/>}
        {running ? "Executing…" : "Run Code (Commander approval)"}
      </button>
      {result && (
        <div className="border border-white/10 bg-black p-3 space-y-2" data-testid="exec-python-result">
          <div className="font-mono-uc text-[10px] uppercase tracking-wider flex items-center gap-3 flex-wrap">
            <span className={result.ok ? "text-emerald-400" : "text-red-400"}>
              {result.ok ? "EXIT 0 · OK" : result.timed_out ? "TIMED OUT" : `EXIT ${result.exit_code ?? "?"} · FAILED`}
            </span>
            <span className="text-white/40">{result.duration_ms}ms</span>
            {result.vault_doc_id && <span className="text-amber-400">ARCHIVED → EXECUTED VAULT</span>}
          </div>
          {result.stdout && <pre className="font-mono-uc text-xs text-emerald-300 whitespace-pre-wrap max-h-48 overflow-y-auto uc-scroll">{result.stdout}</pre>}
          {result.stderr && <pre className="font-mono-uc text-xs text-red-400 whitespace-pre-wrap max-h-40 overflow-y-auto uc-scroll">{result.stderr}</pre>}
        </div>
      )}
    </div>
  );
}

function BrowserRunner() {
  const [url, setUrl] = useState("https://");
  const [running, setRunning] = useState(false);
  const [result, setResult] = useState(null);
  const [ventures, setVentures] = useState([]);
  const [reconVid, setReconVid] = useState("");
  const [reconBusy, setReconBusy] = useState(false);
  const [recon, setRecon] = useState(null);
  const [reconHint, setReconHint] = useState("");
  const [srcQuery, setSrcQuery] = useState("");
  const [srcVid, setSrcVid] = useState("");
  const [srcBusy, setSrcBusy] = useState(false);
  const [srcResult, setSrcResult] = useState(null);

  useEffect(() => {
    venturesApi.list().then(setVentures).catch(() => {});
  }, []);

  const run = async () => {
    setRunning(true);
    setResult(null);
    try {
      const r = await executionApi.runBrowser(url, null, true);
      setResult(r);
      if (r.ok) toast.success(`Page read in ${r.duration_ms}ms · archived to vault`);
      else toast.error(r.error || "Browser run failed");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Browser run failed");
    } finally { setRunning(false); }
  };

  const runRecon = async () => {
    if (!reconVid) return toast.error("Pick a venture first");
    setReconBusy(true);
    setRecon(null);
    setReconHint("");
    try {
      const r = await executionApi.recon(reconVid);
      setRecon(r);
      toast.success(`Scout recon complete on ${r.venture}`);
    } catch (e) {
      const detail = e?.response?.data?.detail || "Recon failed";
      if (detail.includes("No URLs")) {
        setReconHint("This venture has no URLs. Open it in the Venture Cockpit and add a website (metadata) or an account URL, then deploy recon again.");
      }
      toast.error(detail);
    } finally { setReconBusy(false); }
  };

  const runSource = async () => {
    if (!srcQuery.trim()) return toast.error("Enter a search query or URL");
    setSrcBusy(true);
    setSrcResult(null);
    try {
      const isUrl = srcQuery.trim().startsWith("http");
      const r = await executionApi.source({
        venture_id: srcVid || null,
        ...(isUrl ? { url: srcQuery.trim() } : { query: srcQuery.trim() }),
      });
      setSrcResult(r);
      toast.success("Sourced + processed — artifact sealed in vault, task filed in Action Hub");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Sourcing failed");
    } finally { setSrcBusy(false); }
  };

  return (
    <div className="space-y-4">
      <div className="flex gap-2">
        <Input
          data-testid="exec-browser-url"
          value={url}
          onChange={(e) => setUrl(e.target.value)}
          placeholder="https://competitor.com/pricing"
          className="h-10 rounded-none bg-black border-white/10 font-mono-uc text-xs flex-1"
        />
        <button
          data-testid="exec-browser-run-btn"
          onClick={run}
          disabled={running || url.length < 10}
          className="flex items-center gap-2 px-4 border border-emerald-500/40 text-emerald-400 hover:bg-emerald-500/10 font-mono-uc text-[11px] uppercase tracking-widest disabled:opacity-40 transition-colors"
        >
          {running ? <Loader2 size={12} className="animate-spin"/> : <Globe size={12}/>}
          {running ? "Browsing…" : "Read Page"}
        </button>
      </div>
      <div className="text-[10px] text-white/40 font-mono-uc uppercase tracking-wider">Read-only · headless Chromium · private/internal addresses blocked</div>

      {result && result.ok && (
        <div className="border border-white/10 bg-black p-3 space-y-2" data-testid="exec-browser-result">
          <div className="font-serif-display text-base">{result.title}</div>
          <div className="font-mono-uc text-[10px] text-white/40">{result.url} · {result.duration_ms}ms {result.vault_doc_id && "· ARCHIVED"}</div>
          {result.screenshot_b64 && (
            <img src={`data:image/jpeg;base64,${result.screenshot_b64}`} alt="page capture" className="border border-white/10 max-h-64 w-auto" data-testid="exec-browser-screenshot"/>
          )}
          <pre className="font-mono-uc text-xs text-white/70 whitespace-pre-wrap max-h-40 overflow-y-auto uc-scroll">{result.text}</pre>
        </div>
      )}
      {result && !result.ok && (
        <div className="border border-red-500/30 bg-red-500/5 p-3 font-mono-uc text-xs text-red-400" data-testid="exec-browser-error">{result.error}</div>
      )}

      {/* Autonomous Sourcing */}
      <div className="pt-4 border-t border-white/10 space-y-2">
        <div className="font-mono-uc text-[10px] uppercase tracking-widest text-amber-400 flex items-center gap-1.5"><Globe size={11}/> Autonomous Sourcing · Search → retrieve → process into a venture artifact</div>
        <div className="flex gap-2 flex-wrap">
          <Input
            data-testid="exec-source-query"
            value={srcQuery}
            onChange={(e) => setSrcQuery(e.target.value)}
            placeholder="Search query (e.g. 'SBA grant application form') or direct URL"
            className="h-10 rounded-none bg-black border-white/10 font-mono-uc text-xs flex-1 min-w-[240px]"
          />
          <select
            data-testid="exec-source-venture"
            value={srcVid}
            onChange={(e) => setSrcVid(e.target.value)}
            className="h-10 bg-black border border-white/10 text-xs text-white/80 font-mono-uc px-2 outline-none focus:border-amber-500/40"
          >
            <option value="">Portfolio-wide</option>
            {ventures.map((v) => <option key={v.id} value={v.id}>{v.name}</option>)}
          </select>
          <button
            data-testid="exec-source-run-btn"
            onClick={runSource}
            disabled={srcBusy || !srcQuery.trim()}
            className="flex items-center gap-2 px-4 border border-amber-500/40 text-amber-400 hover:bg-amber-500/10 font-mono-uc text-[11px] uppercase tracking-widest disabled:opacity-40 transition-colors h-10"
          >
            {srcBusy ? <Loader2 size={12} className="animate-spin"/> : <Play size={12}/>}
            {srcBusy ? "Sourcing…" : "Source & Process"}
          </button>
        </div>
        {srcResult && (
          <div className="border border-amber-500/20 bg-black p-3 space-y-1" data-testid="exec-source-result">
            <div className="font-serif-display text-base">{srcResult.title}</div>
            <div className="font-mono-uc text-[10px] text-white/40">{srcResult.url}</div>
            <div className="font-mono-uc text-[10px] uppercase tracking-wider text-amber-400">Artifact sealed in vault · task filed in Action Hub</div>
            <pre className="font-mono-uc text-xs text-white/70 whitespace-pre-wrap max-h-32 overflow-y-auto uc-scroll">{srcResult.preview}</pre>
          </div>
        )}
      </div>

      {/* Scout Recon */}
      <div className="pt-4 border-t border-white/10 space-y-2">
        <div className="font-mono-uc text-[10px] uppercase tracking-widest text-blue-400 flex items-center gap-1.5"><Radar size={11}/> Scout Recon · Autonomous read-only pass over a venture's URLs</div>
        <div className="flex gap-2">
          <select
            data-testid="exec-recon-venture"
            value={reconVid}
            onChange={(e) => setReconVid(e.target.value)}
            className="h-10 flex-1 bg-black border border-white/10 text-xs text-white/80 font-mono-uc px-2 outline-none focus:border-blue-500/40"
          >
            <option value="">Select venture…</option>
            {ventures.map((v) => <option key={v.id} value={v.id}>{v.name}</option>)}
          </select>
          <button
            data-testid="exec-recon-run-btn"
            onClick={runRecon}
            disabled={reconBusy || !reconVid}
            className="flex items-center gap-2 px-4 border border-blue-500/40 text-blue-400 hover:bg-blue-500/10 font-mono-uc text-[11px] uppercase tracking-widest disabled:opacity-40 transition-colors"
          >
            {reconBusy ? <Loader2 size={12} className="animate-spin"/> : <Radar size={12}/>}
            {reconBusy ? "Scouting…" : "Deploy Recon"}
          </button>
        </div>
        {reconHint && (
          <div className="p-2 border border-amber-500/30 bg-amber-500/5 text-[11px] text-amber-300" data-testid="exec-recon-hint">{reconHint}</div>
        )}
        {recon && (
          <div className="space-y-1" data-testid="exec-recon-results">
            {recon.findings.map((f, i) => (
              <div key={i} className="p-2 border border-white/5 bg-black text-xs">
                <span className={`font-mono-uc text-[9px] uppercase mr-2 ${f.ok ? "text-emerald-400" : "text-red-400"}`}>{f.ok ? "READ" : "FAIL"}</span>
                <span className="text-white/80">{f.title || f.error}</span>
                <span className="text-white/40 ml-2 font-mono-uc text-[10px]">{f.url}</span>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}

function RunHistory() {
  const [runs, setRuns] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    executionApi.history(20).then(setRuns).catch(() => {}).finally(() => setLoading(false));
  }, []);

  const KIND_LABEL = { python: "PYTHON", browser: "BROWSER", browser_recon: "RECON" };

  return (
    <div className="space-y-1 max-h-72 overflow-y-auto uc-scroll" data-testid="exec-history-list">
      {loading && <div className="text-white/40 text-xs italic py-4">Loading…</div>}
      {!loading && runs.length === 0 && (
        <div className="text-white/40 text-xs italic py-6 text-center">No executions yet. Run code or read a page above.</div>
      )}
      {runs.map((r) => (
        <div key={r.id} className="flex items-center gap-3 p-2 border border-white/5 bg-black text-xs" data-testid={`exec-run-${r.id}`}>
          <span className={`uc-dot ${r.ok ? "bg-emerald-400" : "bg-red-400"}`}></span>
          <span className="font-mono-uc text-[10px] uppercase tracking-wider text-white/70 w-16">{KIND_LABEL[r.kind] || r.kind}</span>
          <span className="text-white/60 flex-1 truncate">
            {r.kind === "python"
              ? (r.result?.stderr ? r.result.stderr.slice(-80) : `exit ${r.result?.exit_code ?? "?"}`)
              : (r.result?.title || r.input?.url || r.input?.venture_id || "")}
          </span>
          <span className="font-mono-uc text-[10px] text-white/40">{r.result?.duration_ms ? `${r.result.duration_ms}ms` : ""}</span>
          <span className="font-mono-uc text-[10px] text-white/40">{relativeTime(r.created_at)}</span>
        </div>
      ))}
    </div>
  );
}
